import React, { useState, useRef, useEffect } from 'react';

interface WhatsAppAudioPlayerProps {
    url: string;
    previewMode?: boolean;
    avatarUrl?: string;
    showDuration?: boolean;
}

export const WhatsAppAudioPlayer: React.FC<WhatsAppAudioPlayerProps> = ({ url, previewMode, avatarUrl, showDuration }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const audioRef = useRef<HTMLAudioElement>(null);
    const [barHeights] = useState(() => Array.from({ length: 60 }).map(() => Math.max(20, Math.random() * 100)));

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateProgress = () => {
            setCurrentTime(audio.currentTime);
            setProgress((audio.currentTime / audio.duration) * 100);
        };

        const setAudioDuration = () => {
            setDuration(audio.duration);
        };

        const onEnded = () => {
            setIsPlaying(false);
            setProgress(0);
            setCurrentTime(0);
        };

        audio.addEventListener('timeupdate', updateProgress);
        audio.addEventListener('loadedmetadata', setAudioDuration);
        audio.addEventListener('ended', onEnded);

        return () => {
            audio.removeEventListener('timeupdate', updateProgress);
            audio.removeEventListener('loadedmetadata', setAudioDuration);
            audio.removeEventListener('ended', onEnded);
        };
    }, []);

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const formatTime = (time: number) => {
        if (isNaN(time)) return "00:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div className="w-full max-w-full bg-white rounded-lg shadow-sm border border-slate-200 py-4 px-3 flex items-center gap-3 select-none overflow-hidden" style={{ overflow: 'hidden' }}>
            <audio ref={audioRef} src={url} preload="metadata" className="hidden" />

            <div className="relative flex-shrink-0">
                {/* Avatar / Icon Placeholder */}
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center relative overflow-hidden">
                    {avatarUrl ? (
                        <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-slate-300">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                        </svg>
                    )}
                </div>
                <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1 border-2 border-white z-10">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 text-white">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.66 9 5v6c0 1.66 1.34 3 3 3z" />
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                    </svg>
                </div>
            </div>

            <div className="flex-1 min-w-0 flex flex-col justify-center">
                <div className="flex items-center gap-3">
                    <button
                        onClick={togglePlay}
                        className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors flex-shrink-0"
                        style={{ pointerEvents: previewMode ? 'auto' : 'none' }}
                    >
                        {isPlaying ? (
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        )}
                    </button>

                    <div className="flex-1 flex flex-col justify-center min-w-0">
                        {/* Fake Waveform */}
                        <div className="flex items-center gap-px h-8 opacity-50 w-full overflow-hidden mb-1.5 pr-2">
                            {barHeights.map((height, i) => (
                                <div
                                    key={i}
                                    className={`flex-1 rounded-full bg-slate-400 transition-all duration-300 ${i / 60 * 100 < progress ? 'bg-blue-500' : ''}`}
                                    style={{ height: `${height}%` }}
                                />
                            ))}
                        </div>

                        <div className="flex justify-between text-xs text-slate-500 h-3 leading-none pr-2">
                            <span className="-ml-0.5">{formatTime(currentTime)}</span>
                            {showDuration && <span className="-mr-0.5">{formatTime(duration)}</span>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
